import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Package, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";

const EXPIRY_BADGE = {
    fresh: "bg-primary/10 text-primary border-primary/30",
    expiring_soon: "bg-amber-500/10 text-amber-700 border-amber-500/30",
    expired: "bg-destructive/10 text-destructive border-destructive/30",
    no_expiry: "bg-secondary text-muted-foreground border-border",
};
const submissionId = () => `product-batch-${farmDateIso().replace(/-/g, "")}-${crypto.randomUUID()}`;

export default function BatchesPage() {
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [data, setData] = useState({ batches: [], kpis: {} });
    const [products, setProducts] = useState([]);
    const [dlg, setDlg] = useState(null);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true); setError("");
        try {
            const [f, s, p] = await Promise.all([
                api.get("/farm").then(r => r.data),
                api.get("/product-batches/summary").then(r => r.data),
                api.get("/products").then(r => r.data),
            ]);
            setFarm(f); setData(s); setProducts(p);
        } catch (requestError) {
            setError(requestError.response?.status === 403 ? "You are not authorized to view product batches." : "Product batches could not be loaded. Please try again.");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    const cur = (v) => formatCurrency(v, farm.currency);

    const save = async () => {
        const body = {
            ...dlg,
            quantity_received: Number(dlg.quantity_received) || 0,
            quantity_remaining: Number(dlg.quantity_remaining ?? dlg.quantity_received) || 0,
            cost_per_unit: Number(dlg.cost_per_unit) || 0,
        };
        if (dlg.id) await api.put(`/product-batches/${dlg.id}`, body);
        else await api.post("/product-batches", body);
        toast.success("Batch saved");
        setDlg(null); load();
    };

    const del = async (b) => {
        if (!window.confirm(`Delete batch ${b.lot_number}?`)) return;
        await api.delete(`/product-batches/${b.id}`);
        toast.success("Deleted"); load();
    };

    const filtered = useMemo(() => {
        return data.batches.filter(b => {
            if (statusFilter !== "all" && b.expiry_status !== statusFilter) return false;
            if (!q) return true;
            const ql = q.toLowerCase();
            return (b.lot_number || "").toLowerCase().includes(ql) ||
                   (b.product_name || "").toLowerCase().includes(ql) ||
                   (b.supplier || "").toLowerCase().includes(ql);
        });
    }, [data.batches, q, statusFilter]);

    if (loading) return <div className="space-y-4" data-testid="batches-page"><Card className="p-6 text-muted-foreground" data-testid="batches-loading-state">{i18n.t("final.7e3275fc04cc")}</Card></div>;
    if (error) return <div className="space-y-4" data-testid="batches-page"><Card className="p-4 border-destructive/40 text-destructive" role="alert" data-testid="batches-error-state">{error}</Card><Button variant="outline" onClick={load}>{i18n.t("final.042c862e4467")}</Button></div>;

    return (
        <div className="space-y-6" data-testid="batches-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="tiny-label">{i18n.t("final.61d2e70b54c2")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("final.cce4cc8c49b7")}</h1>
                    <p className="text-muted-foreground mt-1">{i18n.t("final.a52f821adb26")}</p>
                </div>
                <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                    <DialogTrigger asChild>
                        <Button onClick={() => setDlg({ product_id: "", lot_number: "", quantity_received: "", quantity_remaining: "", cost_per_unit: "", expiry_date: "", received_date: farmDateIso(), supplier: "", notes: "", source_submission_id: submissionId() })} className="h-11" data-testid="add-batch-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("final.be90668bac63")}
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("final.8bf1ef5668b1")}</DialogTitle>
                            <DialogDescription>{i18n.t("final.f1bf035da178")}</DialogDescription>
                        </DialogHeader>
                        {dlg && <div className="space-y-3">
                            <div>
                                <Label className="tiny-label">{i18n.t("final.e45eb77d9c06")}</Label>
                                <Select value={dlg.product_id} onValueChange={v => setDlg({ ...dlg, product_id: v })}>
                                    <SelectTrigger className="h-11 mt-1" data-testid="batch-product-select"><SelectValue placeholder={i18n.t("final.8430451c6c61")} /></SelectTrigger>
                                    <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="tiny-label">{i18n.t("final.90c4ecded385")}</Label><Input value={dlg.lot_number} onChange={e => setDlg({ ...dlg, lot_number: e.target.value })} className="h-11 mt-1 font-mono" data-testid="batch-lot-input" placeholder={i18n.t("final.3c1a07ad42ed")} /></div>
                                <div><Label className="tiny-label">{i18n.t("final.08cc443744dc")}</Label><Input type="date" value={dlg.received_date} onChange={e => setDlg({ ...dlg, received_date: e.target.value })} className="h-11 mt-1" /></div>
                                <div><Label className="tiny-label">{i18n.t("final.937c13e9e29c")}</Label><Input type="number" step="0.01" value={dlg.quantity_received} onChange={e => setDlg({ ...dlg, quantity_received: e.target.value, quantity_remaining: dlg.quantity_remaining || e.target.value })} className="h-11 mt-1 font-mono" data-testid="batch-qty-input" /></div>
                                <div><Label className="tiny-label">{i18n.t("final.be3e12ed5902")}</Label><Input type="number" step="0.01" value={dlg.quantity_remaining} onChange={e => setDlg({ ...dlg, quantity_remaining: e.target.value })} className="h-11 mt-1 font-mono" /></div>
                                <div><Label className="tiny-label">{i18n.t("final.ff5b93665a3a")}{farm.currency})</Label><Input type="number" step="0.01" value={dlg.cost_per_unit} onChange={e => setDlg({ ...dlg, cost_per_unit: e.target.value })} className="h-11 mt-1 font-mono" data-testid="batch-cost-input" /></div>
                                <div><Label className="tiny-label">{i18n.t("final.8d007050838a")}</Label><Input type="date" value={dlg.expiry_date} onChange={e => setDlg({ ...dlg, expiry_date: e.target.value })} className="h-11 mt-1" data-testid="batch-expiry-input" /></div>
                                <div className="col-span-2"><Label className="tiny-label">{i18n.t("final.55edd462872a")}</Label><Input value={dlg.supplier} onChange={e => setDlg({ ...dlg, supplier: e.target.value })} className="h-11 mt-1" /></div>
                                <div className="col-span-2"><Label className="tiny-label">{i18n.t("final.70440046a3dc")}</Label><Input value={dlg.notes} onChange={e => setDlg({ ...dlg, notes: e.target.value })} className="h-11 mt-1" /></div>
                            </div>
                            <Button onClick={save} disabled={!dlg.product_id || !dlg.lot_number} className="w-full h-11" data-testid="save-batch-btn">{i18n.t("final.ec37183a6f52")}</Button>
                        </div>}
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4"><div className="tiny-label">{i18n.t("final.c22d763a0516")}</div><div className="kpi-value text-2xl font-bold mt-2">{data.kpis?.total_batches || 0}</div></Card>
                <Card className="p-4"><div className="tiny-label">{i18n.t("final.4949c4c8f45c")}</div><div className="kpi-value text-2xl font-bold mt-2 font-mono">{cur(data.kpis?.total_value || 0)}</div></Card>
                <Card className={`p-4 ${(data.kpis?.expiring_soon || 0) > 0 ? "bg-amber-500/5" : ""}`}>
                    <div className={`tiny-label ${(data.kpis?.expiring_soon || 0) > 0 ? "text-amber-700" : ""}`}>{i18n.t("final.d6fdbd2508cb")}</div>
                    <div className={`kpi-value text-2xl font-bold mt-2 ${(data.kpis?.expiring_soon || 0) > 0 ? "text-amber-600" : ""}`}>{data.kpis?.expiring_soon || 0}</div>
                </Card>
                <Card className={`p-4 ${(data.kpis?.expired || 0) > 0 ? "bg-destructive/5" : ""}`}>
                    <div className={`tiny-label ${(data.kpis?.expired || 0) > 0 ? "text-destructive" : ""}`}>{i18n.t("final.a689a999a5e6")}</div>
                    <div className={`kpi-value text-2xl font-bold mt-2 ${(data.kpis?.expired || 0) > 0 ? "text-destructive" : ""}`}>{data.kpis?.expired || 0}</div>
                </Card>
            </div>

            <Card className="p-4">
                <div className="flex gap-3 mb-3 flex-wrap">
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder={i18n.t("final.35aafb9af03e")} className="h-10 max-w-xs" data-testid="batch-search" />
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-10 w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{i18n.t("final.9cb29e734a6c")}</SelectItem>
                            <SelectItem value="fresh">{i18n.t("final.f65d3258c00d")}</SelectItem>
                            <SelectItem value="expiring_soon">{i18n.t("final.50b61e0c8341")}</SelectItem>
                            <SelectItem value="expired">{i18n.t("final.a689a999a5e6")}</SelectItem>
                            <SelectItem value="no_expiry">{i18n.t("final.eb744bda0628")}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Table>
                    <TableHeader><TableRow>
                        <TableHead>{i18n.t("final.e8d05704af77")}</TableHead><TableHead>{i18n.t("final.dd3b86d1ef21")}</TableHead>
                        <TableHead className="text-right">{i18n.t("final.27548c4fc95d")}</TableHead><TableHead className="text-right">{i18n.t("final.cc632b5e2fd2")}</TableHead>
                        <TableHead className="text-right">{i18n.t("final.9daa9201229d")}</TableHead><TableHead>{i18n.t("final.ba8f571e0f93")}</TableHead>
                        <TableHead>{i18n.t("final.55edd462872a")}</TableHead><TableHead />
                    </TableRow></TableHeader>
                    <TableBody>
                        {filtered.map(b => (
                            <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                                <TableCell className="font-mono text-xs font-semibold">{b.lot_number}</TableCell>
                                <TableCell><div className="font-semibold">{b.product_name}</div><div className="text-xs text-muted-foreground">{b.product_category}</div></TableCell>
                                <TableCell className="text-right font-mono">{b.quantity_received} {b.unit}</TableCell>
                                <TableCell className="text-right font-mono font-semibold">{b.quantity_remaining} {b.unit}</TableCell>
                                <TableCell className="text-right font-mono">{cur(b.cost_per_unit || 0)}</TableCell>
                                <TableCell>
                                    <div className="flex flex-col gap-1">
                                        <div className="text-xs font-mono">{b.expiry_date || "—"}</div>
                                        <Badge variant="outline" className={`text-xs w-fit ${EXPIRY_BADGE[b.expiry_status]}`}>
                                            {b.expiry_status === "expired" && <AlertTriangle className="w-3 h-3 mr-0.5" />}
                                            {b.expiry_status?.replace("_", " ")}
                                        </Badge>
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs">{b.supplier || "—"}</TableCell>
                                <TableCell className="text-right space-x-1">
                                    <Button size="sm" variant="outline" onClick={() => setDlg(b)}>{i18n.t("final.5301648dcf6b")}</Button>
                                    <Button size="sm" variant="destructive" onClick={() => del(b)}>×</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground"><Package className="w-12 h-12 mx-auto mb-2 opacity-40" />{i18n.t("final.7564829fea86")}</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
}
